// DevHub: ODX Design System — Stacked header matching developer.draeger.com
// Uses CSS classes from @odx/ui/core-theme.css (.odx-header, .odx-button--ghost, .odx-logo, etc.)
//
// The nav model (sections / admin + docs items / visibility) lives in navConfig.ts
// and the header sub-components (NavDropdown, ProfileDropdown, LanguageSwitcher,
// StarredEntitiesDropdown, PageViewToggle, TierBadge, MobileNavMenu, useDropdown)
// live in sibling files; this file is the shell that wires them together.
import { type PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePermission } from '@backstage/plugin-permission-react';
import { appThemeApiRef, configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { commonMessages } from '../../i18n/common';
import { DEVHUB_VERSION } from '../../version';
import { isdpAdminPagePermission, featureEnabled } from '@internal/plugin-isdp-common';
import { SearchModal, useSearchModal } from '../search/SearchModal';
import { CuratorWidget } from '../curator/CuratorWidget';
import { ReportBugButton } from '../feedback/ReportBugButton';
import { RecentActivityTracker } from '../home/RecentActivityTracker';
import { markVisited } from '../home/recentActivity';
import { ROUTES } from '../common/constants/routes';
import { clearSignedIn } from '../auth/portalSession';
import { useLink } from '../common';
import {
  docsItems,
  getVisibleSections,
  sections,
  type NavSection,
} from './navConfig';
import { NavDropdown } from './NavDropdown';
import { ProfileDropdown } from './ProfileDropdown';
import { LanguageSwitcher } from './LanguageSwitcher';
import { StarredEntitiesDropdown } from './StarredEntitiesDropdown';
import { PageViewToggle } from './PageViewToggle';
import { TierBadge } from './TierBadge';
import { MobileNavMenu } from './MobileNavMenu';
import './Root.css';

// Search shortcut is Cmd+K on macOS, Ctrl+K elsewhere — show the right hint.
const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');
const SEARCH_SHORTCUT_HINT = IS_MAC ? '⌘K' : 'Ctrl K';
const SEARCH_SHORTCUT_LABEL = IS_MAC ? '⌘K' : 'Ctrl+K';

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const location = useLocation();
  const link = useLink();
  const { t } = useTranslationRef(commonMessages);
  const identityApi = useApi(identityApiRef);
  const partnerPortal = featureEnabled(useApi(configApiRef), 'partnerPortal');
  // app.version isn't in the browser config schema (see version.ts) — use the constant.
  const appVersion = DEVHUB_VERSION;
  const { allowed: isAdmin } = usePermission({
    permission: isdpAdminPagePermission,
  });
  const { open: searchOpen, openSearch, closeSearch } = useSearchModal();

  // DELICATE Home personalization: stamp "last visit" once per session. This
  // snapshots the *previous* value first (see recentActivity.ts) so Home's
  // "N updates since your last visit" nudge stays stable. Purely client-side.
  useEffect(() => {
    markVisited();
  }, []);

  // Global search shortcut — Cmd+K (mac) / Ctrl+K (win/linux) opens the
  // top-right search popup. Root wraps every authenticated page, so this binds
  // once for the whole app. "/" also opens it when not typing in a field.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (cmdK) {
        e.preventDefault();
        openSearch();
        return;
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        const typing =
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target?.isContentEditable;
        if (!typing) {
          e.preventDefault();
          openSearch();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [openSearch]);

  // Dark mode — use Backstage's appThemeApiRef as the single source of truth
  const appThemeApi = useApi(appThemeApiRef);
  const [isDark, setIsDark] = useState(() => {
    return appThemeApi.getActiveThemeId() === 'draeger-dark';
  });

  // Subscribe to theme changes (from Settings page or other sources)
  useEffect(() => {
    const subscription = appThemeApi.activeThemeId$().subscribe(themeId => {
      setIsDark(themeId === 'draeger-dark');
    });
    return () => subscription.unsubscribe();
  }, [appThemeApi]);

  // ISDP CUSTOM: force LIGHT as the default. If the user has never picked a
  // theme, seed light so core (MUI) pages don't follow the OS dark preference.
  // (Done via setActiveThemeId — NOT by overriding appThemeApiRef, which crashes
  // createApp in this Backstage version.)
  useEffect(() => {
    if (appThemeApi.getActiveThemeId() === undefined) {
      appThemeApi.setActiveThemeId('draeger-light');
    }
  }, [appThemeApi]);

  const toggleDark = useCallback(() => {
    const newTheme = isDark ? 'draeger-light' : 'draeger-dark';
    appThemeApi.setActiveThemeId(newTheme);
  }, [appThemeApi, isDark]);

  const [userGroups, setUserGroups] = useState<string[]>([]);

  useEffect(() => {
    identityApi.getBackstageIdentity().then(identity => {
      setUserGroups(
        identity.ownershipEntityRefs.filter(r => r.startsWith('group:')),
      );
    });
  }, [identityApi]);

  const isActivePath = (path: string) => {
    if (path === ROUTES.home) return location.pathname === ROUTES.home;
    return location.pathname.startsWith(path);
  };

  // Section-aware active check so EVERY route highlights its parent top-nav section
  // (not just each section's own landing path): a route is "in" a section when it matches
  // the section's path or any `match` prefix, and isn't caught by its `exclude` list
  // (used where sections overlap, e.g. the Guides doc living under /docs).
  const isSectionActive = (section: NavSection): boolean => {
    if (section.path === ROUTES.home) return location.pathname === ROUTES.home;
    const prefixes = [section.path, ...(section.match ?? [])];
    const hit = prefixes.some(p => location.pathname.startsWith(p));
    if (!hit) return false;
    return !(section.exclude ?? []).some(p => location.pathname.startsWith(p));
  };

  const visibleSections = getVisibleSections(userGroups);
  const docsSection = sections.find(s => s.path === ROUTES.overview.docs);
  const isDocsActive = docsSection ? isSectionActive(docsSection) : false;

  return (
    <div className="odx-app-root">
      <div className="odx-app">
        {/* Sticky single-bar header: logo (left) + right-aligned nav + actions */}
        <div className="devhub-sticky-header">
          <div className="odx-header">
            <a className="devhub-home-link" {...link(ROUTES.home)}>
              <odx-logo aria-label="Dräger" />
              <span className="odx-header__title">DevHub</span>
            </a>
            {appVersion && (
              <a
                className="devhub-version-badge"
                title={`DevHub v${appVersion} — view the changelog`}
                {...link(`${ROUTES.announcements}#v${appVersion}`)}
              >
                v{appVersion}
              </a>
            )}

            <div className="odx-header__content" />

            {/* Right-aligned navigation — the IA sections as plain links.
                "Documentation" is a jump-off dropdown (Getting Started / Curated / All Docs). */}
            <nav className="devhub-nav-inline">
              {visibleSections.map(section =>
                section.path === ROUTES.overview.docs ? (
                  <NavDropdown
                    key={section.path}
                    label={(t as unknown as (k: string) => string)(section.labelKey)}
                    items={docsItems}
                    isActive={isDocsActive}
                  />
                ) : (
                  <odx-button
                    key={section.path}
                    variant="ghost"
                    // React 18 does NOT reflect `className` onto custom elements, so the
                    // `.is-active` highlight must be set via the `class` attribute (which
                    // React does setAttribute for custom elements). `undefined` (not '')
                    // avoids leaving an empty class attribute on inactive items.
                    class={isSectionActive(section) ? 'is-active' : undefined}
                    {...link(section.to ?? section.path)}
                  >
                    {(t as unknown as (k: string) => string)(section.labelKey)}
                  </odx-button>
                ),
              )}
            </nav>

            <div className="odx-action-group">
              <TierBadge groups={userGroups} />
              <LanguageSwitcher />
              <StarredEntitiesDropdown />
              <odx-button
                variant="ghost"
                // `class`, NOT `className`: React 18 does not reflect className onto a
                // custom element, so this rendered `classname="…"` and BOTH the
                // .devhub-search-trigger rule in Root.css and every selector looking for
                // it (9 command-palette e2e tests) matched nothing. See odx.d.ts.
                class="devhub-search-trigger"
                type="button"
                onClick={openSearch}
                title={`${t('search.hint')} (${SEARCH_SHORTCUT_LABEL})`}
              >
                <odx-icon slot="prefix" className="odx-icon" name="core::search" />
                <kbd className="devhub-kbd-hint">{SEARCH_SHORTCUT_HINT}</kbd>
              </odx-button>
              <ProfileDropdown
                isAdmin={isAdmin}
                isDark={isDark}
                toggleDark={toggleDark}
                onSignOut={async () => {
                  await identityApi.signOut();
                  // Clear the readable signed-in flag, like the other sign-out paths do
                  // (ExternalWorld, NoInternalAccess) — otherwise a signed-out visitor is
                  // still treated as signed in when deciding which root to boot.
                  clearSignedIn();
                  // Landing on the marketing portal only makes sense when it is shipped.
                  // While it is out of the release /products has no route, so signing out
                  // dropped the user on the branded 404 instead of the sign-in screen —
                  // exactly where someone who just signed out wants to be if they are
                  // signing back in. '/' renders the sign-in screen when unauthenticated.
                  window.location.href = partnerPortal
                    ? ROUTES.public.products
                    : ROUTES.home;
                }}
              />
              {/* Hamburger — visible only below the nav breakpoint (see Root.css) */}
              <MobileNavMenu
                sections={visibleSections}
                isActivePath={isActivePath}
                isSectionActive={isSectionActive}
              />
            </div>
          </div>
        </div>

        <main className="odx-app-main">
          {children}
          <PageViewToggle />
        </main>
      </div>
      {/* ISDP CUSTOM: headless recorder for DELICATE Home personalization —
          records docs/catalog entities the user opens (localStorage only,
          identity-light). Renders nothing. */}
      <RecentActivityTracker />
      <SearchModal open={searchOpen} onClose={closeSearch} />
      {/* ISDP CUSTOM: the DevHub Curator (advisory-only chat). Root renders ONLY for
          internal, non-previewing users (see AppBody in App.tsx), so mounting here
          scopes the widget to the internal hub — never the public/partner portal,
          where partners reach the Curator via MCP instead. */}
      <CuratorWidget />
      {/* ISDP CUSTOM: "Report a bug" launcher (Sentry User Feedback). Bottom-LEFT,
          opposite the curator widget, so the two float buttons balance rather than
          stack. Scoped to the internal hub for the same reason as the curator. */}
      <ReportBugButton />
    </div>
  );
};
